import { Fragment, useEffect, useMemo } from "react";
import { CircleMarker, MapContainer, Polyline, Tooltip, useMap } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import { MapTileLayers } from "./MapBaseLayers";


const DEFAULT_CENTER: LatLngExpression = [33.6844, 73.0479]; // Rawalpindi/Islamabad

export interface TrackSegment {
  id: string;
  coordinates: [number, number][];
  color: string;
}

export interface TrackStop {
  id: string;
  name: string;
  lat: number;
  lon: number;
  color: string;
  isEndpoint: boolean;
}

function FitToTrack({ points }: { points: LatLngExpression[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 14); return; }
    map.fitBounds(points as [number, number][], { padding: [36, 36] });
    // Only re-fit when the set of points actually changes (line/direction
    // switch) — not on every pan/zoom interaction inside the map itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points), map]);
  return null;
}

function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    requestAnimationFrame(() => map.invalidateSize());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

export default function RouteTrackMap({
  segments, stops, theme,
}: {
  segments: TrackSegment[];
  stops: TrackStop[];
  theme?: "dark" | "light";
}) {

  const allPoints = useMemo<LatLngExpression[]>(() => {
    const pts: LatLngExpression[] = [];
    segments.forEach((s) => s.coordinates.forEach((c) => pts.push(c)));
    stops.forEach((s) => pts.push([s.lat, s.lon]));
    return pts;
  }, [segments, stops]);

  return (
    <MapContainer
      center={allPoints[0] ?? DEFAULT_CENTER}
      zoom={13}
      style={{ height: "100%", width: "100%" }}
      zoomControl={true}
      attributionControl={false}
      scrollWheelZoom={true}
    >
      <MapTileLayers theme={theme === "light" ? "light" : "dark"} />
      <InvalidateOnResize />
      {segments.map((s) => (
        <Fragment key={s.id}>
          <Polyline
            positions={s.coordinates}
            pathOptions={{ color: "#000", weight: 9, opacity: 0.22, lineCap: "round", lineJoin: "round", interactive: false }}
          />
          <Polyline
            positions={s.coordinates}
            pathOptions={{ color: `#${s.color}`, weight: 5, opacity: 0.95, lineCap: "round", lineJoin: "round" }}
          />
        </Fragment>
      ))}
      {stops.map((s) => (
        <CircleMarker
          key={s.id}
          center={[s.lat, s.lon]}
          radius={s.isEndpoint ? 8 : 5.5}
          pathOptions={{
            color: `#${s.color}`, weight: s.isEndpoint ? 4 : 3,
            fillColor: "#fff", fillOpacity: 1,
          }}
        >
          <Tooltip direction="top" offset={[0, -8]} opacity={1}>{s.name}</Tooltip>
        </CircleMarker>
      ))}
      <FitToTrack points={allPoints} />
    </MapContainer>
  );
}
